import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Layers, Plus, Trash2, Edit3, Printer, Usb, Monitor, CheckCircle2 } from "lucide-react";
import { getStationPrinters, setStationPrinter } from "@/lib/restaurantPrint";
import { connectPrinterFor, isSupported as serialSupported } from "@/lib/thermalPrinter";

export default function KitchenStations() {
  const { currentCompany } = useAuth();
  const cid = currentCompany?.id;
  const [stations, setStations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category_names: [] });
  const [printers, setPrinters] = useState({});

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/restaurant/stations"), api.get("/categories")]);
      setStations(s.data);
      setCategories((c.data || []).map((x) => x.name).filter(Boolean));
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); setPrinters(getStationPrinters(cid)); /* eslint-disable-next-line */ }, [cid]);

  const openNew = () => { setEditing(null); setForm({ name: "", category_names: [] }); setDlg(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, category_names: s.category_names || [] }); setDlg(true); };

  const toggleCat = (name) => setForm((f) => ({
    ...f,
    category_names: f.category_names.includes(name) ? f.category_names.filter((x) => x !== name) : [...f.category_names, name],
  }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("اسم القسم مطلوب");
    try {
      if (editing) await api.put(`/restaurant/stations/${editing.id}`, form);
      else await api.post("/restaurant/stations", form);
      toast.success("تم الحفظ"); setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (s) => {
    if (!window.confirm(`حذف القسم «${s.name}»؟`)) return;
    try { await api.delete(`/restaurant/stations/${s.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  // Printer assignment (stored on this device)
  const setMode = (stationKey, mode) => {
    setStationPrinter(cid, stationKey, { ...(printers[stationKey] || {}), mode });
    setPrinters(getStationPrinters(cid));
    if (mode === "browser") toast.success("سيُطبع على الطابعة الافتراضية لهذا الجهاز");
  };
  const connectUsb = async (stationKey, label) => {
    try {
      if (!serialSupported()) return toast.error("متصفحك لا يدعم USB المباشر — استخدم Chrome/Edge على الكمبيوتر");
      await connectPrinterFor(stationKey);
      setStationPrinter(cid, stationKey, { mode: "usb", label });
      setPrinters(getStationPrinters(cid));
      toast.success(`تم ربط طابعة USB بـ «${label}»`);
    } catch (e) { toast.error("تعذّر ربط الطابعة — تأكد من توصيلها واختيارها"); }
  };

  const PrinterRow = ({ stationKey, label }) => {
    const cfg = printers[stationKey] || {};
    const mode = cfg.mode || "browser";
    return (
      <div className="flex items-center gap-2 flex-wrap mt-2 pt-2 border-t" data-testid={`printer-row-${stationKey}`}>
        <span className="text-xs text-muted-foreground flex items-center gap-1"><Printer className="w-3.5 h-3.5" /> الطابعة:</span>
        <button
          onClick={() => setMode(stationKey, "browser")}
          className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${mode === "browser" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted-foreground/10"}`}
          data-testid={`printer-browser-${stationKey}`}
        ><Monitor className="w-3.5 h-3.5" /> افتراضية الجهاز</button>
        <button
          onClick={() => connectUsb(stationKey, label)}
          className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${mode === "usb" ? "bg-emerald-600 text-white" : "bg-muted hover:bg-muted-foreground/10"}`}
          data-testid={`printer-usb-${stationKey}`}
        ><Usb className="w-3.5 h-3.5" /> {mode === "usb" ? "USB مربوطة" : "ربط طابعة USB"}</button>
        {mode === "usb" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="stations-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Layers className="w-6 h-6 text-primary" /> أقسام المطبخ</h1>
          <p className="text-sm text-muted-foreground mt-1">اربط كل قسم بمجموعات الأصناف، وحدّد طابعته — فتذهب تذكرة كل صنف لقسمه المختص فقط</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-station-btn"><Plus className="w-4 h-4" /> قسم جديد</Button>
      </div>

      {stations.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <Layers className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد أقسام — أنشئ أول قسم (مثال: قسم عربي، قسم شرقي، مشروبات)</div>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {stations.map((s) => (
            <div key={s.id} className="border rounded-lg p-4 bg-card" data-testid={`station-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-bold text-lg flex items-center gap-2"><Layers className="w-4 h-4 text-primary" /> {s.name}</div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(s)} className="text-muted-foreground hover:text-primary" data-testid={`edit-station-${s.id}`}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => del(s)} className="text-muted-foreground hover:text-red-500" data-testid={`del-station-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(s.category_names || []).length === 0
                  ? <span className="text-xs text-muted-foreground italic">لم تُربط أي مجموعة أصناف بعد</span>
                  : (s.category_names || []).map((c) => <span key={c} className="text-[11px] px-2 py-0.5 rounded bg-primary/10 text-primary">{c}</span>)}
              </div>
              <PrinterRow stationKey={s.id} label={s.name} />
            </div>
          ))}
        </div>
      )}

      <div className="border rounded-lg p-4 bg-muted/20">
        <div className="font-bold flex items-center gap-2"><Printer className="w-4 h-4" /> إيصال العميل + أصناف بلا قسم</div>
        <p className="text-xs text-muted-foreground mt-1">الأصناف التي لا تنتمي لأي قسم تُطبع على «المطبخ» العام. اضبط طابعتها وطابعة إيصال العميل هنا.</p>
        <PrinterRow stationKey="_default" label="المطبخ العام" />
        <PrinterRow stationKey="_receipt" label="إيصال العميل" />
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="station-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل قسم" : "قسم جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>اسم القسم</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="قسم عربي / قسم شرقي / مشروبات / حلويات" data-testid="station-name" /></div>
            <div>
              <Label>مجموعات الأصناف التابعة لهذا القسم</Label>
              {categories.length === 0 ? (
                <div className="text-xs text-muted-foreground mt-1">لا توجد مجموعات أصناف بعد — أنشئها من «مجموعات الأصناف» ثم اربطها هنا.</div>
              ) : (
                <div className="mt-1 p-2 border rounded max-h-56 overflow-y-auto grid grid-cols-2 gap-1 bg-muted/20">
                  {categories.map((c) => {
                    const on = form.category_names.includes(c);
                    return (
                      <label key={c} className={`flex items-center gap-1.5 p-1.5 rounded cursor-pointer text-sm ${on ? "bg-primary/10 border border-primary/30" : "hover:bg-background border border-transparent"}`} data-testid={`station-cat-${c}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleCat(c)} />
                        <span>{c}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button><Button onClick={save} data-testid="save-station-btn">حفظ</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
